/**
 * Utility functions for formatting chart labels based on time options
 */

export type TimeOption = 'day' | 'week' | 'month' | 'quarter' | 'all';

/**
 * Get week number for a given date
 * Uses the same calculation as getPeriodNumber() in dateUtils.ts to ensure consistency
 * across dashboard and charts pages.
 */
function getWeekNumber(date: Date): number {
  // This matches the calculation in dateUtils.ts getPeriodNumber() for 'week'
  // Week number is calculated from the start of the year
  const yearStart = new Date(date.getFullYear(), 0, 1);

  // Calculate the start of the week for the given date (Monday-based)
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Convert Sunday = 0 to Monday = 0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);

  const diff = d.getTime() - yearStart.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 7)) + 1;
}

/**
 * Format time label based on actual date
 * @param date - The date for the period
 * @param timeOption - Selected time option
 * @returns Formatted label (e.g., "W52", "Jan 2024", "Jan 15")
 */
export function formatTimeLabel(
  date: Date,
  timeOption: TimeOption
): string {
  switch (timeOption) {
    case 'day': {
      // Format: "Jan 15" or "Dec 31"
      const month = date.toLocaleDateString(undefined, { month: 'short' });
      const day = date.getDate();
      return `${month} ${day}`;
    }
    case 'week': {
      // Format: "W52" (week number)
      const weekNumber = getWeekNumber(date);
      return `W${weekNumber}`;
    }
    case 'month':
    case 'quarter':
    case 'all': {
      // Format: "Jan 2024" or "Dec 2023"
      const month = date.toLocaleDateString(undefined, { month: 'short' });
      const year = date.getFullYear();
      return `${month} ${year}`;
    }
    default:
      return date.toLocaleDateString();
  }
}

/**
 * Generate array of time labels for a given number of periods
 * @deprecated Use calculateTimePeriods from chartsUtils instead, which provides dates
 */
export function generateTimeLabels(
  timeOption: TimeOption,
  totalPeriods: number = 12
): string[] {
  const labels: string[] = [];
  const now = new Date();
  for (let i = 0; i < totalPeriods; i++) {
    // This is a fallback - prefer using calculateTimePeriods with actual dates
    labels.push(formatTimeLabel(now, timeOption));
  }
  return labels;
}
